import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getLiveMatch,
  formatMapName,
  formatPhase,
  calculateKD,
  formatWeaponName,
  LiveMatch,
  Player
} from '../../services/LiveService';
import '../../styles/live.css';
import Loading from '../../components/Loading';
import useLocalStorage from 'use-local-storage';

const LiveMatchPage: React.FC = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const [match, setMatch] = useState<LiveMatch | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isDark] = useLocalStorage<boolean>("isDark", true);
  
    useEffect(() => {
      const fetchMatchDetails = async () => {
        try {
          setLoading(true);
          if (!matchId) {
            throw new Error('Match ID is required');
          }
          const data = await getLiveMatch(matchId);
          setMatch(data);
          setError(null);
        } catch (err) {
          console.error('Error fetching match details:', err);
          setError('Match not found or no longer live');
          setMatch(null);
        } finally {
          setLoading(false);
        }
      };
  
      // Fetch match immediately
      fetchMatchDetails();
  
      // Then set up polling every 5 seconds
      const interval = setInterval(fetchMatchDetails, 5000);
  
      // Clean up on unmount
      return () => clearInterval(interval);
    }, [matchId]);
  
    if (loading) {
      return <Loading />;
    }
  
    if (error || !match) {
      return (
        <div className={`live-match-container ${isDark ? 'theme-dark' : 'theme-light'}`}>
          <h1>Live Match</h1>
          <div className="alert alert-danger">
            {error || 'Match not found'}
          </div>
          <Link to="/live" className="btn btn-primary">
            Back to Live Matches
          </Link>
        </div>
      );
    }
  
    // Filter players by team
    const ctPlayers = match.players.filter(p => p.team === 'CT');
    const tPlayers = match.players.filter(p => p.team === 'T');
    
    // Sort players by kills
    const sortPlayers = (players: Player[]) => {
      return [...players].sort((a, b) => b.kills - a.kills);
    };

    // Filter out all knife-related weapons
    const filterWeapons = (weapons: string[]) => {
        return weapons.filter(weapon => !weapon.includes('knife') && !weapon.includes('bayonet'));
    };
    
  
    return (
      <div className={`live-match-container ${isDark ? 'theme-dark' : 'theme-light'}`}>
        <div className={`match-header ${isDark ? 'dark-mode' : 'light-mode'}`}>
          <Link to="/live" className="back-button">
            ← Back to Live Matches
          </Link>
          
          <h1 className="match-title">Live Match</h1>
          
          {match.isStreaming && (
            <div className="stream-info">
              <span className="live-badge">LIVE</span>
              <a 
                href={`https://twitch.tv/${match.streamerName}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="stream-link"
              >
                Watch on Twitch
              </a>
            </div>
          )}
        </div>
        
        <div className="map-info">
          <span className="map-name">{formatMapName(match.mapName)}</span>
          <span className="round-info">Round {match.currentRound}</span>
          <span className="phase-info">{formatPhase(match.gamePhase)}</span>
        </div>
        
        <div className="team-scores">
          <div className="team team-ct">
            <div className="team-logo-container">
              {match.ctTeam.logoUrl && (
                <img 
                  src={match.ctTeam.logoUrl} 
                  alt={match.ctTeam.teamName} 
                  className="team-logo"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
            </div>
            <span className="team-name">{match.ctTeam.teamName}</span>
            <span className="team-score ct-score">{match.ctTeam.score}</span>
          </div>
          
          <div className="versus">VS</div>
          
          <div className="team team-t">
            <div className="team-logo-container">
              {match.tTeam.logoUrl && (
                <img 
                  src={match.tTeam.logoUrl} 
                  alt={match.tTeam.teamName} 
                  className="team-logo"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
            </div>
            <span className="team-name">{match.tTeam.teamName}</span>
            <span className="team-score t-score">{match.tTeam.score}</span>
          </div>
        </div>
        
        <div className="scoreboard-container">
          <div className="scoreboard-section">
            <h3 className="team-ct-header">{match.ctTeam.teamName}</h3>
            <table className="scoreboard-table team-ct-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>K</th>
                  <th>A</th>
                  <th>D</th>
                  <th>K/D</th>
                  <th>$</th>
                  <th>Weapons</th>
                </tr>
              </thead>
              <tbody>
                {sortPlayers(ctPlayers).map((player) => (
                  <tr key={player.steamId} className={!player.isAlive ? 'player-dead' : ''}>
                    <td>
                      <div className="player-name">
                        <span className={`player-status ${player.isAlive ? 'alive' : 'dead'}`}></span>
                        <Link to={`/player/${player.steamId}`}>{player.name}</Link>
                      </div>
                    </td>
                    <td>{player.kills}</td>
                    <td>{player.assists}</td>
                    <td>{player.deaths}</td>
                    <td>{calculateKD(player.kills, player.deaths)}</td>
                    <td className="player-money">${player.money}</td>
                    <td>
                      <div className="weapons-list">
                        {filterWeapons(player.weapons).map((weapon, index) => (
                          <span key={index} className="weapon-name">
                            {formatWeaponName(weapon)}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="scoreboard-section">
            <h3 className="team-t-header">{match.tTeam.teamName}</h3>
            <table className="scoreboard-table team-t-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>K</th>
                  <th>A</th>
                  <th>D</th>
                  <th>K/D</th>
                  <th>$</th>
                  <th>Weapons</th>
                </tr>
              </thead>
              <tbody>
                {sortPlayers(tPlayers).map((player) => (
                  <tr key={player.steamId} className={!player.isAlive ? 'player-dead' : ''}>
                    <td>
                      <div className="player-name">
                        <span className={`player-status ${player.isAlive ? 'alive' : 'dead'}`}></span>
                        <Link to={`/player/${player.steamId}`}>{player.name}</Link>
                      </div>
                    </td>
                    <td>{player.kills}</td>
                    <td>{player.assists}</td>
                    <td>{player.deaths}</td>
                    <td>{calculateKD(player.kills, player.deaths)}</td>
                    <td className="player-money">${player.money}</td>
                    <td>
                      <div className="weapons-list">
                        {filterWeapons(player.weapons).map((weapon, index) => (
                          <span key={index} className="weapon-name">
                            {formatWeaponName(weapon)}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="powered-by">
          Powered by PowerStats Live
        </div>
      </div>
    );
  };
  
  export default LiveMatchPage;